import React, { Component } from "react";
import { Modal } from "semantic-ui-react";
import { AxiosResponse } from "axios";
import { OngoingCourse } from "../../../stores/EnrolmentStore/Types";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import { errorNotification, notification } from "../../../utils/helpers";
import {
  getRandomPersonalizationMessage,
  nextPersonalizationProgress,
  personalizationMessages,
} from "./helpers";
import { PersonalizationModalContent } from "./PersonalizationModalContent";
import { PersonalizationGeneratingContent } from "./PersonalizationGeneratingContent";

interface Props {
  enrolment: OngoingCourse;
  onPersonalizationStateChange: (state: string) => void;
  showModal?: boolean;
}

interface State {
  loading: boolean;
  isSkipBusy: boolean;
  isEnableBusy: boolean;
  isCloseModal: boolean;
  personalizationState: string;
  personalizationName: string;
  personalizationCountry: string;
  personalizationWorkingOn: string;
  personalizationMustImprove: string;
  errors: {
    personalizationName?: boolean;
    personalizationCountry?: boolean;
    personalizationWorkingOn?: boolean;
    personalizationMustImprove?: boolean;
  };
  personalizationProgress: number;
  randomMessage: string;
}

class PersonalizationModal extends Component<Props, State> {
  timerId: NodeJS.Timeout | null = null;
  messageIntervalId: NodeJS.Timeout | null = null;
  maxPollTimeInSeconds: number = 30;
  pollingStartedAt: number | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      isSkipBusy: false,
      isEnableBusy: false,
      isCloseModal: false,
      personalizationState: props.showModal
        ? "asking"
        : props.enrolment.personalization_state,
      personalizationName: "",
      personalizationCountry: "",
      personalizationWorkingOn: "",
      personalizationMustImprove: "",
      errors: {},
      personalizationProgress: 0,
      randomMessage: personalizationMessages[0],
    };
  }

  componentDidMount() {
    this.getPersonalizationConfig();
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.showModal && this.props.showModal) {
      this.setState({ personalizationState: "asking" });
    }
  }

  componentWillUnmount() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }

    if (this.messageIntervalId) {
      clearInterval(this.messageIntervalId);
    }
  }

  updatePersonalizationStateFromAPI = (data: any) => {
    const apiState = data.personalization_state;
    const personalizationState =
      this.props.showModal &&
      !["complete", "generating", "generating_progress"].includes(apiState)
        ? "asking"
        : apiState;

    this.setState({
      personalizationState,
      personalizationName: data.personalization_name || "",
      personalizationCountry: data.personalization_country || "",
      personalizationWorkingOn: data.personalization_working_on || "",
      personalizationMustImprove: data.personalization_must_improve || "",
    });
    this.props.onPersonalizationStateChange(personalizationState);
  };

  getPersonalizationConfig = () => {
    this.setState({ loading: true });

    return getRequest(
      `/api/enrolments/${this.props.enrolment.id}/personalization`,
    )
      .then((response: AxiosResponse) => {
        this.updatePersonalizationStateFromAPI(response.data);
        return response.data;
      })
      .catch((error) => {
        errorNotification(
          "Error when fetching personalisation",
          JSON.stringify(error?.response?.data),
        );
        return null;
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  skipPersonalization = () => {
    this.setState({ isSkipBusy: true });

    postRequest(
      `/api/enrolments/${this.props.enrolment.id}/personalization/skip`,
      {},
    )
      .then((response: AxiosResponse) => {
        this.setState({
          personalizationState: response.data.personalization_state,
        });
        this.props.onPersonalizationStateChange(
          response.data.personalization_state,
        );
      })
      .catch((error) => {
        errorNotification(
          "Error when skipping personalisation",
          JSON.stringify(error?.response?.data),
        );
      })
      .finally(() => {
        this.setState({ isSkipBusy: false });
      });
  };

  activatePersonalization = () => {
    const errors = {
      personalizationName: !this.state.personalizationName.trim(),
      personalizationCountry: !this.state.personalizationCountry.trim(),
      personalizationWorkingOn: !this.state.personalizationWorkingOn.trim(),
      personalizationMustImprove: !this.state.personalizationMustImprove.trim(),
    };

    const hasErrors = Object.values(errors).some((error) => error);

    if (hasErrors) {
      this.setState({ errors });
      return;
    }

    this.setState({ isEnableBusy: true, errors: {} });

    postRequest(
      `/api/enrolments/${this.props.enrolment.id}/personalization/activate`,
      {
        personalization_name: this.state.personalizationName,
        personalization_country: this.state.personalizationCountry,
        personalization_working_on: this.state.personalizationWorkingOn,
        personalization_must_improve: this.state.personalizationMustImprove,
      },
    )
      .then((response: AxiosResponse) => {
        const personalizationState = response.data.personalization_state;

        this.setState({
          personalizationState: personalizationState,
        });
        this.increasePersonalizationProgress();
        this.startPolling();
        this.startMessageRotation();
        this.props.onPersonalizationStateChange(personalizationState);
      })
      .catch((error) => {
        errorNotification(
          "Error when activating personalisation",
          JSON.stringify(error?.response?.data),
        );
      })
      .finally(() => {
        this.setState({ isEnableBusy: false });
      });
  };

  increasePersonalizationProgress = () => {
    this.setState((prevState) => ({
      personalizationProgress: nextPersonalizationProgress(
        prevState.personalizationProgress,
      ),
    }));
  };

  startPolling = () => {
    if (this.timerId) clearTimeout(this.timerId);
    this.pollingStartedAt = Date.now();

    const poll = () => {
      this.increasePersonalizationProgress();
      this.getPersonalizationConfig().then((data) => {
        const timedOut =
          this.pollingStartedAt &&
          Date.now() - this.pollingStartedAt >=
            this.maxPollTimeInSeconds * 1000;

        if (!data) {
          if (timedOut) {
            this.stopPolling();
            return;
          }

          this.timerId = setTimeout(poll, 3000);
          return;
        }

        const nextState = data.personalization_state;

        if (nextState !== "generating") {
          this.stopPollingTimers();
          return;
        }

        if (timedOut) {
          this.stopPolling();
          return;
        }

        this.timerId = setTimeout(poll, 3000);
      });
    };

    this.timerId = setTimeout(poll, 3000);
  };

  stopPollingTimers = () => {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.pollingStartedAt = null;

    if (this.messageIntervalId) {
      clearInterval(this.messageIntervalId);
      this.messageIntervalId = null;
    }
  };

  stopPolling = () => {
    this.stopPollingTimers();
    this.setState({
      personalizationState: "generating_progress",
      isCloseModal: false,
    });
    this.props.onPersonalizationStateChange("generating_progress");
    notification(
      "Personalisation in progress. You will be notified once complete.",
      "notification",
    );
  };

  startMessageRotation = () => {
    if (this.messageIntervalId) clearInterval(this.messageIntervalId);

    this.messageIntervalId = setInterval(() => {
      this.setState({ randomMessage: getRandomPersonalizationMessage() });
    }, 2500);
  };

  closeModal = () => {
    if (this.state.personalizationState === "asking") {
      this.skipPersonalization();
    }
  };

  handleFieldChange = (
    field:
      | "personalizationName"
      | "personalizationCountry"
      | "personalizationWorkingOn"
      | "personalizationMustImprove",
    value: string,
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      [field]: value,
      errors: { ...prevState.errors, [field]: false },
    }));
  };

  render() {
    const {
      personalizationState,
      loading,
      isEnableBusy,
      isSkipBusy,
      errors,
      personalizationName,
      personalizationCountry,
      personalizationWorkingOn,
      personalizationMustImprove,
      personalizationProgress,
      randomMessage,
      isCloseModal,
    } = this.state;
    const isOpen =
      this.props.showModal ||
      personalizationState === "asking" ||
      personalizationState === "generating" ||
      isCloseModal;

    return (
      <Modal
        size="small"
        open={isOpen}
        className={`personalization-modal pz-modal ${
          personalizationState === "generating" ? "pz-load" : ""
        }`}
        closeIcon={false}
        closeOnDimmerClick={personalizationState !== "generating"}
        closeOnEscape={personalizationState !== "generating"}
        onClose={this.closeModal}
      >
        <Modal.Content>
          {personalizationState === "asking" && (
            <button
              className="pz-close"
              type="button"
              onClick={this.closeModal}
              aria-label="Close"
              disabled={isSkipBusy}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" strokeLinecap="round" />
              </svg>
            </button>
          )}
          {personalizationState === "asking" && (
            <PersonalizationModalContent
              title="Personalise this course"
              buttonLabel="Personalise my course"
              loading={loading}
              isButtonDisabled={isEnableBusy || isSkipBusy || loading}
              buttonClassName={`btn btn-blue ${isEnableBusy ? "loading" : ""}`}
              personalizationName={personalizationName}
              personalizationCountry={personalizationCountry}
              personalizationWorkingOn={personalizationWorkingOn}
              personalizationMustImprove={personalizationMustImprove}
              errors={errors}
              onSubmit={this.activatePersonalization}
              onFieldChange={this.handleFieldChange}
            />
          )}
          {personalizationState === "generating" && (
            <PersonalizationGeneratingContent
              progress={personalizationProgress}
              message={randomMessage}
            />
          )}
        </Modal.Content>
      </Modal>
    );
  }
}

export default PersonalizationModal;
